import { useCallback, useState } from 'react'
import { Platform } from 'react-native'
import * as ImagePicker from 'expo-image-picker'
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import {
  B2B_ORG_LOGO_MAX_FILE_BYTES,
} from '../constants/accountAdmin.constants'
import { useAccountAdminCopy } from './useAccountAdminCopy'

interface ApiEnvelope {
  success?: boolean
  error?: string
}

function appendImageField(
  form: FormData,
  fieldName: string,
  asset: ImagePicker.ImagePickerAsset,
) {
  const uri = asset.uri
  const name = asset.fileName ?? `${fieldName}.jpg`
  const type = asset.mimeType ?? 'image/jpeg'
  form.append(fieldName, {
    uri,
    name,
    type,
  } as unknown as Blob)
}

/**
 * Web: `fetch(uri)` → blob → FormData. Returns `null` if over size limit.
 */
async function fetchWebLogoBlob(
  asset: ImagePicker.ImagePickerAsset,
): Promise<Blob | null> {
  if (Platform.OS !== 'web' || typeof fetch !== 'function') return null
  const res = await fetch(asset.uri)
  const blob = await res.blob()
  if (blob.size > B2B_ORG_LOGO_MAX_FILE_BYTES) return null
  return blob
}

export function useB2bImageUpload(accountId: string | null, remainingLogoSlots: number) {
  const copy = useAccountAdminCopy()
  const apiClient = useApiClient()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [removingLogoIndex, setRemovingLogoIndex] = useState<number | null>(null)

  const requestLibraryPermission = useCallback(async () => {
    const { granted } = await ImagePicker.requestMediaLibraryPermissionsAsync()
    return granted === true
  }, [])

  const pickAssets = useCallback(
    async (maxSelectable: number) => {
      const cap = Math.max(0, maxSelectable)
      if (cap === 0) return []
      const ok = await requestLibraryPermission()
      if (!ok) return []
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        quality: 0.85,
        allowsMultipleSelection: true,
        selectionLimit: cap,
      })
      if (result.canceled || result.assets.length === 0) return []
      return result.assets.slice(0, cap)
    },
    [requestLibraryPermission],
  )

  const uploadLogos = useCallback(async () => {
    if (!accountId) return
    setUploadError(null)
    if (remainingLogoSlots <= 0) {
      setUploadError('Maximum logos reached. Remove one to add more.')
      return
    }
    const assets = await pickAssets(remainingLogoSlots)
    if (assets.length === 0) return
    for (const asset of assets) {
      const knownSize = typeof asset.fileSize === 'number' ? asset.fileSize : null
      if (knownSize !== null && knownSize > B2B_ORG_LOGO_MAX_FILE_BYTES) {
        setUploadError(copy.LOGO_FILE_TOO_LARGE)
        return
      }
    }
    setIsUploading(true)
    try {
      const form = new FormData()
      if (Platform.OS === 'web') {
        for (const asset of assets) {
          const blob = await fetchWebLogoBlob(asset)
          if (!blob) {
            setUploadError(copy.LOGO_FILE_TOO_LARGE)
            return
          }
          form.append('logo', blob, asset.fileName ?? 'upload.jpg')
        }
      } else {
        for (const asset of assets) {
          appendImageField(form, 'logo', asset)
        }
      }
      const res = await apiClient<ApiEnvelope>(API_ENDPOINTS.accounts.logo(accountId), {
        method: 'POST',
        data: form,
      })
      if (!(res.data as ApiEnvelope)?.success) {
        throw new Error((res.data as ApiEnvelope)?.error || 'Upload failed')
      }
    } catch (e) {
      setUploadError(extractErrorMessage(e, 'Upload failed'))
    } finally {
      setIsUploading(false)
    }
  }, [accountId, apiClient, copy, pickAssets, remainingLogoSlots])

  const removeLogoAtIndex = useCallback(
    async (index: number) => {
      if (!accountId) return
      setUploadError(null)
      setRemovingLogoIndex(index)
      try {
        const res = await apiClient<ApiEnvelope>(API_ENDPOINTS.accounts.logoAtIndex(accountId, index), {
          method: 'DELETE',
        })
        if (!(res.data as ApiEnvelope)?.success) {
          throw new Error((res.data as ApiEnvelope)?.error || 'Remove failed')
        }
      } catch (e) {
        setUploadError(extractErrorMessage(e, 'Remove failed'))
      } finally {
        setRemovingLogoIndex(null)
      }
    },
    [accountId, apiClient],
  )

  const pickSingleAsset = useCallback(async () => {
    const ok = await requestLibraryPermission()
    if (!ok) return null
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.9,
      allowsMultipleSelection: false,
    })
    if (result.canceled || result.assets.length === 0) return null
    return result.assets[0]
  }, [requestLibraryPermission])

  const uploadBanner = useCallback(async () => {
    if (!accountId) return
    setUploadError(null)
    const asset = await pickSingleAsset()
    if (!asset) return
    const knownSize = typeof asset.fileSize === 'number' ? asset.fileSize : null
    if (knownSize !== null && knownSize > B2B_ORG_LOGO_MAX_FILE_BYTES) {
      setUploadError(copy.LOGO_FILE_TOO_LARGE)
      return
    }
    setIsUploading(true)
    try {
      const form = new FormData()
      if (Platform.OS === 'web') {
        const blob = await fetchWebLogoBlob(asset)
        if (!blob) {
          setUploadError(copy.LOGO_FILE_TOO_LARGE)
          return
        }
        form.append('banner', blob, asset.fileName ?? 'banner.jpg')
      } else {
        appendImageField(form, 'banner', asset)
      }
      const res = await apiClient<ApiEnvelope>(API_ENDPOINTS.accounts.banner(accountId), {
        method: 'POST',
        data: form,
      })
      if (!(res.data as ApiEnvelope)?.success) {
        throw new Error((res.data as ApiEnvelope)?.error || 'Upload failed')
      }
    } catch (e) {
      setUploadError(extractErrorMessage(e, 'Upload failed'))
    } finally {
      setIsUploading(false)
    }
  }, [accountId, apiClient, copy, pickSingleAsset])

  const removeBanner = useCallback(async () => {
    if (!accountId) return
    setUploadError(null)
    setIsUploading(true)
    try {
      const res = await apiClient<ApiEnvelope>(API_ENDPOINTS.accounts.banner(accountId), {
        method: 'DELETE',
      })
      if (!(res.data as ApiEnvelope)?.success) {
        throw new Error((res.data as ApiEnvelope)?.error || 'Remove failed')
      }
    } catch (e) {
      setUploadError(extractErrorMessage(e, 'Remove failed'))
    } finally {
      setIsUploading(false)
    }
  }, [accountId, apiClient])

  return {
    isUploading,
    uploadError,
    setUploadError,
    uploadLogos,
    removeLogoAtIndex,
    removingLogoIndex,
    uploadBanner,
    removeBanner,
  }
}
